import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  SupportTicket,
  useMarkTicketRead,
  useSendMessage,
  useSupportMessages,
} from '@/hooks/useSupportChat';
import { useAuth } from '@/contexts/AuthContext';
import { MessageBubble } from './MessageBubble';
import { BotFlow } from './BotFlow';
import { statusBadgeClasses, statusLabelKey } from './ticketStatus';

interface Props {
  ticket: SupportTicket;
  onBack?: () => void;
  asAdmin?: boolean;
}

export const TicketConversation = ({ ticket, onBack, asAdmin }: Props) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: messages = [] } = useSupportMessages(ticket.id);
  const sendMessage = useSendMessage();
  const markRead = useMarkTicketRead();
  const [text, setText] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  // Marquer comme lu à l'ouverture
  useEffect(() => {
    const side = asAdmin ? 'admin' : 'user';
    if ((asAdmin && ticket.unread_for_admin) || (!asAdmin && ticket.unread_for_user)) {
      markRead.mutate({ ticketId: ticket.id, side });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id, ticket.unread_for_admin, ticket.unread_for_user]);

  const canSend =
    asAdmin || ticket.status === 'open' || ticket.status === 'pending_admin' || ticket.status === 'resolved';

  const send = async () => {
    if (!text.trim()) return;
    await sendMessage.mutateAsync({
      ticketId: ticket.id,
      content: text.trim(),
      senderType: asAdmin ? 'admin' : 'user',
    });
    setText('');
    // Si admin répond pour la 1ère fois, passe à 'open'
    if (asAdmin && ticket.status === 'pending_admin') {
      // côté admin, l'update est faite dans la page Support
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#DBE2EA] bg-white">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack} className="lg:hidden">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#37699E] truncate font-['Lexend_Deca']">
            {ticket.subject || t('supportChat.newTicket')}
          </p>
          <p className="text-xs text-[#5A6B7A]">
            #{ticket.id.slice(0, 8)}
            {ticket.category ? ` · ${t(`supportChat.cat.${ticket.category}`)}` : ''}
          </p>
        </div>
        <Badge className={statusBadgeClasses(ticket.status)}>
          {t(statusLabelKey(ticket.status))}
        </Badge>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-[#F8F9FA]">
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            isOwn={asAdmin ? m.sender_type === 'admin' : m.sender_type === 'user'}
          />
        ))}
      </div>

      {ticket.status === 'bot' && !asAdmin && <BotFlow ticket={ticket} />}

      {canSend && ticket.status !== 'closed' && (
        <div className="border-t border-[#DBE2EA] p-3 bg-white">
          <div className="flex gap-2 items-end">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={t('supportChat.writeMessage')}
              rows={1}
              className="resize-none border-[#DBE2EA] focus:border-[#0880AE] rounded-lg min-h-[44px] max-h-32"
            />
            <Button
              onClick={send}
              disabled={!text.trim() || sendMessage.isPending}
              className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] rounded-lg h-11 w-11 p-0 flex-shrink-0"
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
